import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { API, auth, inputCls, Field, Modal, PageHead, PrimaryBtn, Paginator, errMsg, fmtDate } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';
import PlayerKpiBar from './players/PlayerKpiBar';
import ExpiringAlert from './players/ExpiringAlert';
import PlayersTable from './players/PlayersTable';
import SubscriptionsTable from './players/SubscriptionsTable';
import BulkToolbar from './players/BulkToolbar';
import BulkCardsModal from './players/BulkCardsModal';
import SubscriptionActionModal from './players/SubscriptionActionModal';
import CategoryResolutionSection from './players/CategoryResolutionSection';
import SubscriptionHistoryPanel from './players/SubscriptionHistoryPanel';
import DangerZoneDeleteAllModal from './players/DangerZoneDeleteAllModal';
import AcademyImportModal from './players/AcademyImportModal';
import PremiumDatePicker from '../../components/ui/PremiumDatePicker';
import { subscriptionBadge, isExpiringWithin, SUB_STATUS_LABELS, PAYMENT_STATUS_LABELS } from './players/subscriptionUtils';
import { canUseWhatsApp } from '../../lib/whatsapp';
import { WhatsAppMessageModal } from '../../components/whatsapp/WhatsAppMessageModal';

const buildDefaultCategoryResolutions = (suggestions) => {
  const map = {};
  (suggestions || []).forEach((s) => {
    map[s.name] = s.suggested_category_id
      ? { action: 'link', category_id: s.suggested_category_id }
      : { action: 'create', min_age: s.inferred_min_age, max_age: s.inferred_max_age };
  });
  return map;
};

const POSITIONS = { goalkeeper: 'حارس مرمى', defender: 'مدافع', midfielder: 'وسط', forward: 'مهاجم' };
const FEET = { right: 'اليمنى', left: 'اليسرى', both: 'كلتاهما' };
const FORM_STEPS = ['البيانات الشخصية والتواصل', 'البيانات الرياضية والفئة', 'الاشتراك والدفع'];
const PLAYER_PAYMENT_METHODS = { cash: 'كاش', network: 'شبكة', transfer: 'تحويل', electronic: 'إلكتروني', other: 'أخرى' };
const DURATION_UNIT_LABELS = { day: 'يوم', week: 'أسبوع', month: 'شهر', year: 'سنة' };
const CUSTOM_PACKAGE_VALUE = '__custom__';
const addDaysIso = (dateStr, days) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const packageDurationDays = (pkg) => (Number(pkg.duration_value) || 1) * ({ day: 1, week: 7, month: 30, year: 365 }[pkg.duration_unit] || 30);
const computeFinalAmount = (basePrice, discountType, discountValue) => {
  const base = Number(basePrice) || 0;
  const dv = Number(discountValue) || 0;
  if (!discountType || !dv) return base;
  const final = discountType === 'percent' ? base * (1 - Math.min(dv, 100) / 100) : Math.max(0, base - dv);
  return Math.round(final * 100) / 100;
};
const computeAgeFromBirth = (birthDate) => {
  if (!birthDate) return null;
  const b = new Date(birthDate);
  if (isNaN(b.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - b.getFullYear();
  if (today.getMonth() < b.getMonth() || (today.getMonth() === b.getMonth() && today.getDate() < b.getDate())) age -= 1;
  return age;
};
const suggestCategory = (age, categories) => {
  if (age == null) return null;
  const matches = categories.filter((c) => (c.min_age == null || age >= c.min_age) && (c.max_age == null || age <= c.max_age));
  if (!matches.length) return null;
  return matches.sort((a, b) => ((a.max_age ?? 999) - (a.min_age ?? -1)) - ((b.max_age ?? 999) - (b.min_age ?? -1)))[0];
};

const BarcodeCardModal = ({ player, club, catName, onClose }) => {
  const [orientation, setOrientation] = useState('landscape');
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => { if (player) setOrientation('landscape'); }, [player]);
  if (!player) return null;
  const logoUrl = club?.logo_file_id ? `${API}/files/${club.logo_file_id}` : null;
  const portrait = orientation === 'portrait';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="player-barcode-modal">
      <div className="absolute inset-0 bg-black/50 print:hidden" onClick={onClose} />
      <div className="relative w-full max-w-md">
        <div className="mb-3 flex justify-center gap-2 print:hidden" data-testid="player-card-orientation-toggle">
          <button onClick={() => setOrientation('landscape')} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${!portrait ? 'bg-[#16233b] text-white' : 'bg-white text-slate-600 border border-slate-200'}`} data-testid="player-card-orientation-landscape">عرضي</button>
          <button onClick={() => setOrientation('portrait')} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${portrait ? 'bg-[#16233b] text-white' : 'bg-white text-slate-600 border border-slate-200'}`} data-testid="player-card-orientation-portrait">طولي</button>
        </div>
        {portrait ? (
          <div className="player-card-print player-card-portrait mx-auto overflow-hidden rounded-2xl bg-white shadow-2xl" data-testid="player-print-card">
            <div className="bg-[#16233b] px-5 py-5 text-center">
              {logoUrl && <img src={logoUrl} alt="شعار" className="mx-auto h-12 w-12 rounded-lg bg-white/10 object-cover" />}
              <div className="mt-2 text-sm font-bold text-white">{club?.name || club?.club_name || 'المنشأة الرياضية'}</div>
              <div className="text-[11px] text-slate-300">بطاقة لاعب</div>
            </div>
            <div className="flex flex-col items-center gap-2 px-5 py-5" dir="rtl">
              {player.photo_url ? <img src={player.photo_url} alt="" className="h-24 w-24 rounded-full bg-slate-100 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                : <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-slate-100 text-2xl font-bold text-[#16233b]">{(player.name || '؟').slice(0, 2)}</div>}
              <div className="text-base font-bold text-slate-900">{player.name}</div>
              <div className="text-xs text-slate-500">{catName(player.category_id)} · {POSITIONS[player.position] || 'لاعب'} · رقم {player.jersey_number ?? '—'}</div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${player.is_active ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{player.is_active ? 'منتسب فعّال' : 'غير فعّال'}</span>
            </div>
            <div className="border-t border-slate-100 px-5 py-4 text-center">
              <QRCodeSVG value={player.id} size={140} level="M" className="mx-auto" />
              <div className="mt-1 text-[9px] tracking-wider text-slate-400" dir="ltr">{player.id}</div>
            </div>
            <div className="bg-slate-50 px-5 py-2 text-center text-[10px] text-slate-400">تُستخدم هذه البطاقة لتسجيل الحضور والتحقق من الانتساب عبر مسح رمز QR</div>
          </div>
        ) : (
          <div className="player-card-print overflow-hidden rounded-2xl bg-white shadow-2xl" data-testid="player-print-card">
            <div className="flex items-center gap-3 bg-[#16233b] px-5 py-4">
              {logoUrl && <img src={logoUrl} alt="شعار" className="h-12 w-12 rounded-lg bg-white/10 object-cover" />}
              <div>
                <div className="text-sm font-bold text-white">{club?.name || club?.club_name || 'المنشأة الرياضية'}</div>
                <div className="text-[11px] text-slate-300">بطاقة لاعب</div>
              </div>
            </div>
            <div className="flex items-center gap-4 px-5 py-4" dir="rtl">
              {player.photo_url ? <img src={player.photo_url} alt="" className="h-16 w-16 rounded-xl bg-slate-100 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                : <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-xl font-bold text-[#16233b]">{(player.name || '؟').slice(0, 2)}</div>}
              <div className="min-w-0 space-y-0.5">
                <div className="text-base font-bold text-slate-900">{player.name}</div>
                <div className="text-xs text-slate-500">{catName(player.category_id)} · {POSITIONS[player.position] || 'لاعب'} · رقم {player.jersey_number ?? '—'}</div>
                <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${player.is_active ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{player.is_active ? 'منتسب فعّال' : 'غير فعّال'}</span>
              </div>
            </div>
            <div className="border-t border-slate-100 px-5 py-3 text-center">
              <QRCodeSVG value={player.id} size={120} level="M" className="mx-auto" />
              <div className="mt-1 text-[9px] tracking-wider text-slate-400" dir="ltr">{player.id}</div>
            </div>
            <div className="bg-slate-50 px-5 py-2 text-center text-[10px] text-slate-400">تُستخدم هذه البطاقة لتسجيل الحضور والتحقق من الانتساب عبر مسح رمز QR</div>
          </div>
        )}
        <div className="mt-3 flex justify-center gap-2 print:hidden">
          <button onClick={() => window.print()} className="flex items-center gap-2 rounded-lg bg-[#16233b] px-5 py-2 text-sm font-semibold text-white" data-testid="player-card-print-btn"><Icon name="document" className="w-4 h-4" /> طباعة البطاقة</button>
          <button onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-600" data-testid="player-card-close-btn">إغلاق</button>
        </div>
      </div>
      <style>{`
        .player-card-portrait { width: 300px; }
        @media print {
          body * { visibility: hidden !important; }
          .player-card-print, .player-card-print * { visibility: visible !important; }
          .player-card-print { position: fixed !important; top: 20px; right: 0; left: 0; margin: auto; width: 420px; box-shadow: none !important; }
          .player-card-print.player-card-portrait { width: 300px !important; }
        }
      `}</style>
    </div>
  );
};

const Avatar = ({ p, size = 'h-16 w-16' }) => p.photo_url ? (
  <img src={p.photo_url} alt="" className={`${size} rounded-full bg-slate-100 object-cover`} onError={(e) => { e.target.style.display = 'none'; }} />
) : (
  <div className={`${size} flex items-center justify-center rounded-full bg-[#16233b]/10 text-xs font-bold text-[#16233b]`}>{(p.name || '؟').slice(0, 2)}</div>
);

const selectCls = 'appearance-none rounded-lg border border-slate-200 bg-white py-1.5 pr-2 pl-7 text-sm text-slate-700 outline-none';
const Select = ({ value, onChange, children, testId }) => (
  <div className="relative">
    <select value={value} onChange={onChange} className={selectCls} data-testid={testId}>{children}</select>
    <Icon name="chevronDown" className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
  </div>
);
const EXPIRING_LABELS = { expiring_30: 'تنتهي خلال 30 يوماً', expiring_7: 'تنتهي خلال 7 أيام' };

export default function PlayersPage() {
  const { club, user, selectedSeasonId } = useOutletContext() || {};
  const seasonQS = selectedSeasonId && selectedSeasonId !== 'all' ? `?season_id=${selectedSeasonId}` : '';
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'subscriptions' ? 'subscriptions' : 'players';

  const [players, setPlayers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [sessionsUsage, setSessionsUsage] = useState(null);
  const [basePrice, setBasePrice] = useState(null); // سعر الباقة الأصلي قبل الخصم (لإعادة حساب السعر النهائي محلياً)
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => params.get('q') || '');
  const [catFilter, setCatFilter] = useState('');
  const [posFilter, setPosFilter] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState(null);
  const [formStep, setFormStep] = useState(1);
  const [quickCat, setQuickCat] = useState(null); // {name, min_age, max_age, busy, error}
  const [viewing, setViewing] = useState(null);
  const [showSubHistory, setShowSubHistory] = useState(false);
  const [barcodePlayer, setBarcodePlayer] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [importState, setImportState] = useState(null); // {preview, busy, error}
  const [importSource, setImportSource] = useState('auto'); // auto | tayyar | rakaz
  const [rakazState, setRakazState] = useState(null); // {preview, busy, error, overrides, excluded}
  const [academyState, setAcademyState] = useState(null); // {preview, busy, error, resolutions, excluded, recordPayments}
  const [lastImport, setLastImport] = useState(null); // آخر عملية استيراد قابلة للتراجع
  const [undoBusy, setUndoBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const fileRef = useRef(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };
  const canDelete = Boolean(user?.is_super_admin || user?.role === 'admin');

  const submitQuickCategory = async () => {
    if (!quickCat?.name?.trim()) { setQuickCat({ ...quickCat, error: 'اسم الفئة مطلوب' }); return; }
    if (!quickCat.min_age || !quickCat.max_age) { setQuickCat({ ...quickCat, error: 'العمر الأدنى والأقصى مطلوبان لاقتراح الفئة تلقائياً للاعبين' }); return; }
    setQuickCat({ ...quickCat, busy: true, error: null });
    try {
      const res = await axios.post(`${API}/categories`, {
        name: quickCat.name.trim(), description: '',
        min_age: parseInt(quickCat.min_age, 10), max_age: parseInt(quickCat.max_age, 10),
      }, auth());
      const created = res.data;
      setCategories((prev) => [...prev, created]);
      setForm((f) => ({ ...f, category_id: created.id }));
      setQuickCat(null);
      notify('تمت إضافة الفئة وحُددت للاعب — وستجدها أيضاً في صفحة الفرق والفئات');
    } catch (err) {
      setQuickCat((q) => ({ ...q, busy: false, error: errMsg(err, 'فشل إضافة الفئة') }));
    }
  };
  const canFinance = Boolean(user?.is_super_admin || ['admin', 'manager', 'treasurer'].includes(user?.role));
  const canWhatsApp = canUseWhatsApp(user);
  const canDeleteAllPlayers = Boolean(user?.is_users_admin);
  const [dangerZoneOpen, setDangerZoneOpen] = useState(false);
  const [dangerZoneAccordionOpen, setDangerZoneAccordionOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [subAction, setSubAction] = useState(null); // {type, player}
  const [inviteLink, setInviteLink] = useState(null); // {playerName, url}
  const [inviteLoading, setInviteLoading] = useState(false);
  const [bulkCardsPlayers, setBulkCardsPlayers] = useState(null);
  const [waProfilePlayer, setWaProfilePlayer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [p, c, pk] = await Promise.allSettled([axios.get(`${API}/players${seasonQS}`, auth()), axios.get(`${API}/categories`, auth()), axios.get(`${API}/subscription-packages`, auth())]);
    if (p.status === 'fulfilled') setPlayers(Array.isArray(p.value.data) ? p.value.data : []);
    if (c.status === 'fulfilled') setCategories(Array.isArray(c.value.data) ? c.value.data : []);
    if (pk.status === 'fulfilled') setPackages(Array.isArray(pk.value.data) ? pk.value.data : []);
    setLoading(false);
  }, [seasonQS]);
  useEffect(() => { load(); }, [load]);

  const catName = (id) => categories.find((c) => c.id === id)?.name || '—';
  const setView = (v) => setParams((prev) => { const n = new URLSearchParams(prev); n.set('view', v); return n; });

  const openForm = (p) => {
    setEditing(p || null);
    setFormStep(1);
    setBasePrice(p?.subscription_amount ?? null);
    setForm(p ? {
      ...p,
      birth_date: p.birth_date ? String(p.birth_date).slice(0, 10) : '',
      subscription_start: p.subscription_start ? String(p.subscription_start).slice(0, 10) : '',
      subscription_end: p.subscription_end ? String(p.subscription_end).slice(0, 10) : '',
      subscription_package_id: p.subscription_package_id || '',
      subscription_discount_type: p.subscription_discount_type || '',
      subscription_discount_value: p.subscription_discount_value ?? '',
      subscription_discount_reason: p.subscription_discount_reason || '',
      subscription_payment_method: p.subscription_payment_method || '',
      subscription_payment_reference: p.subscription_payment_reference || '',
      sessions_per_week: p.sessions_per_week ?? '',
      sessions_total_override: p.sessions_total_override ?? '',
    } : {
      name: '', category_id: '', position: '', jersey_number: '', birth_date: '', nationality: '', height: '', weight: '', preferred_foot: '', photo_url: '', phone: '',
      subscription_start: '', subscription_end: '', subscription_status: '', subscription_package: '', subscription_package_id: '', subscription_amount: '', subscription_paid_amount: '', subscription_payment_status: '', subscription_notes: '',
      subscription_discount_type: '', subscription_discount_value: '', subscription_discount_reason: '', subscription_payment_method: '', subscription_payment_reference: '',
      guardian_name: '', guardian_phone: '', home_address: '',
      sessions_per_week: '', sessions_total_override: '',
    });
    setFormError(null); setModal(true);
  };

  useEffect(() => {
    if (loading) return;
    const highlightId = params.get('highlight');
    if (highlightId && params.get('edit') === '1') {
      const p = players.find((x) => x.id === highlightId);
      if (p) {
        openForm(p);
        setParams((prev) => { const n = new URLSearchParams(prev); n.delete('highlight'); n.delete('edit'); return n; }, { replace: true });
      }
    }
  }, [loading, players]);

  useEffect(() => {
    if (!viewing) { setSessionsUsage(null); return; }
    let alive = true;
    axios.get(`${API}/player-sessions/usage?player_id=${viewing.id}`, auth())
      .then((r) => { if (alive) setSessionsUsage(r.data?.items?.[0] || null); })
      .catch(() => { if (alive) setSessionsUsage(null); });
    return () => { alive = false; };
  }, [viewing]);

  const onPackageSelect = (packageId) => {
    if (packageId === CUSTOM_PACKAGE_VALUE || !packageId) {
      setForm((f) => ({ ...f, subscription_package_id: '' }));
      return;
    }
    const pkg = packages.find((x) => x.id === packageId);
    if (!pkg) return;
    setBasePrice(pkg.price);
    const finalAmount = computeFinalAmount(pkg.price, form.subscription_discount_type, form.subscription_discount_value);
    setForm((f) => ({
      ...f,
      subscription_package_id: pkg.id,
      subscription_package: pkg.name,
      subscription_amount: finalAmount,
      subscription_end: addDaysIso(f.subscription_start, packageDurationDays(pkg)),
    }));
  };

  const onDiscountChange = (patch) => {
    setForm((f) => {
      const next = { ...f, ...patch };
      if (basePrice != null) next.subscription_amount = computeFinalAmount(basePrice, next.subscription_discount_type, next.subscription_discount_value);
      return next;
    });
  };

  const uploadSubscriptionReceipt = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editing) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/players/${editing.id}/subscription/receipt`, fd, auth());
      notify('تم رفع الإيصال بنجاح');
    } catch (err) { notify(errMsg(err, 'فشل رفع الإيصال')); }
  };

  const submit = async (e, skipSubscription = false) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.category_id) { setFormError('اسم اللاعب والفئة حقلان مطلوبان'); return; }
    if (!skipSubscription && form.subscription_start && form.subscription_end && form.subscription_end < form.subscription_start) { setFormError('تاريخ نهاية الاشتراك يجب أن يكون بعد تاريخ البداية'); return; }
    if (!skipSubscription && ((form.subscription_amount && Number(form.subscription_amount) < 0) || (form.subscription_paid_amount && Number(form.subscription_paid_amount) < 0))) { setFormError('قيم الاشتراك لا يمكن أن تكون سالبة'); return; }
    const amount = form.subscription_amount !== '' && form.subscription_amount != null ? Number(form.subscription_amount) : null;
    const paid = form.subscription_paid_amount !== '' && form.subscription_paid_amount != null ? Number(form.subscription_paid_amount) : null;
    const computedPaymentStatus = !amount ? (form.subscription_payment_status || null) : (paid >= amount ? 'paid' : paid > 0 ? 'partial' : 'unpaid');
    const payload = {
      name: form.name.trim(),
      category_id: form.category_id,
      position: form.position || null,
      jersey_number: form.jersey_number ? Number(form.jersey_number) : null,
      birth_date: form.birth_date || null,
      nationality: form.nationality?.trim() || null,
      height: form.height ? Number(form.height) : null,
      weight: form.weight ? Number(form.weight) : null,
      preferred_foot: form.preferred_foot || null,
      photo_url: form.photo_url?.trim() || null,
      phone: form.phone?.trim() || null,
      subscription_start: skipSubscription ? null : (form.subscription_start || null),
      subscription_end: skipSubscription ? null : (form.subscription_end || null),
      subscription_status: skipSubscription ? null : (form.subscription_status || null),
      subscription_package: skipSubscription ? null : (form.subscription_package?.trim() || null),
      subscription_package_id: skipSubscription ? null : (form.subscription_package_id || null),
      sessions_per_week: skipSubscription || form.sessions_per_week === '' || form.sessions_per_week === null ? null : Number(form.sessions_per_week),
      sessions_total_override: skipSubscription || form.sessions_total_override === '' || form.sessions_total_override === null ? null : Number(form.sessions_total_override),
      subscription_amount: skipSubscription ? null : amount,
      subscription_paid_amount: skipSubscription ? null : paid,
      subscription_payment_status: skipSubscription ? null : computedPaymentStatus,
      subscription_discount_type: skipSubscription ? null : (form.subscription_discount_type || null),
      subscription_discount_value: skipSubscription ? null : (form.subscription_discount_value !== '' && form.subscription_discount_value != null ? Number(form.subscription_discount_value) : null),
      subscription_discount_reason: skipSubscription ? null : (form.subscription_discount_reason?.trim() || null),
      subscription_payment_method: skipSubscription ? null : (form.subscription_payment_method || null),
      subscription_payment_reference: skipSubscription ? null : (form.subscription_payment_reference?.trim() || null),
      subscription_notes: skipSubscription ? null : (form.subscription_notes?.trim() || null),
      guardian_name: form.guardian_name?.trim() || null,
      guardian_phone: form.guardian_phone?.trim() || null,
      home_address: form.home_address?.trim() || null,
    };
    try {
      let res;
      if (editing) res = await axios.put(`${API}/players/${editing.id}`, payload, auth());
      else res = await axios.post(`${API}/players`, payload, auth());
      setModal(false);
      notify(res.data?.category_auto_transferred ? 'تم الحفظ — وتم نقل اللاعب تلقائياً لفئة عمرية مطابقة لتجاوزه حدود فئته السابقة' : 'تم الحفظ بنجاح');
      load();
    } catch (err) { setFormError(errMsg(err, 'فشل الحفظ')); }
  };

  const toggleActive = async (p) => {
    try { await axios.put(`${API}/players/${p.id}`, { is_active: !p.is_active }, auth()); notify(p.is_active ? 'تم تعطيل اللاعب' : 'تم تفعيل اللاعب'); load(); }
    catch (err) { notify(errMsg(err, 'فشل التحديث')); }
  };

  const toggleArchive = async (p) => {
    try { await axios.put(`${API}/players/${p.id}`, { is_archived: !p.is_archived }, auth()); notify(p.is_archived ? 'تم إلغاء الأرشفة' : 'تم أرشفة اللاعب'); load(); }
    catch (err) { notify(errMsg(err, 'فشل التحديث')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/players/${confirmDel.id}`, auth()); notify('تم حذف اللاعب'); setConfirmDel(null); load(); }
    catch (err) { notify(errMsg(err, 'تعذر الحذف')); setConfirmDel(null); }
  };

  const inviteGuardian = async (p) => {
    setInviteLoading(true);
    try {
      const res = await axios.post(`${API}/players/${p.id}/guardian-invite`, {}, auth());
      setInviteLink({ playerName: p.name, url: `${window.location.origin}${res.data.invite_path}` });
    } catch (err) { notify(errMsg(err, 'فشل توليد رابط الدعوة')); }
    setInviteLoading(false);
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink.url);
    notify('تم نسخ رابط الدعوة');
  };

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleSelectAll = (ids) => setSelected((s) => { const allIn = ids.every((id) => s.has(id)); const n = new Set(s); ids.forEach((id) => (allIn ? n.delete(id) : n.add(id))); return n; });
  const clearSelection = () => setSelected(new Set());
  const selectedPlayers = players.filter((p) => selected.has(p.id));

  const bulkUpdate = async (partial, label) => {
    try {
      await Promise.all(selectedPlayers.map((p) => axios.put(`${API}/players/${p.id}`, partial, auth())));
      notify(label); clearSelection(); load();
    } catch (err) { notify(errMsg(err, 'فشل تنفيذ العملية الجماعية')); }
  };
  const bulkAssignCategory = (catId) => bulkUpdate({ category_id: catId }, 'تم تعيين الفئة للمحددين');
  const bulkActivate = () => bulkUpdate({ is_active: true }, 'تم تفعيل اللاعبين المحددين');
  const bulkDeactivate = () => bulkUpdate({ is_active: false }, 'تم تعطيل اللاعبين المحددين');
  const bulkArchive = () => bulkUpdate({ is_archived: true }, 'تمت أرشفة اللاعبين المحددين');
  const bulkExport = () => {
    const rows = selectedPlayers.map((p) => ({ 'الاسم': p.name, 'الرقم': p.jersey_number, 'الفئة': catName(p.category_id), 'المركز': POSITIONS[p.position] || '', 'الجوال': p.phone || p.guardian_phone || '', 'حالة اللاعب': p.is_active ? 'نشط' : 'معطل', 'حالة الاشتراك': subscriptionBadge(p).label }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'اللاعبون');
    XLSX.writeFile(wb, 'players_export.xlsx');
  };

  const runSubAction = async (fn) => { try { await fn(); notify('تم تنفيذ الإجراء بنجاح'); load(); } catch (err) { notify(errMsg(err, 'فشل تنفيذ الإجراء')); } };
  const onPay = (p) => setSubAction({ type: 'payment', player: p });
  const onRenew = (p) => setSubAction({ type: 'renew', player: p });
  const onExtend = (p) => setSubAction({ type: 'extend', player: p });
  const onCancelSub = (p) => setSubAction({ type: 'cancel', player: p });
  const onActivateSub = (p) => runSubAction(() => axios.post(`${API}/players/${p.id}/subscription/activate`, {}, auth()));
  const onPauseResume = (p, pause) => runSubAction(() => axios.post(`${API}/players/${p.id}/subscription/${pause ? 'pause' : 'resume'}`, {}, auth()));

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/players/excel-template`, { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'players_template.xlsx'; a.click(); URL.revokeObjectURL(url);
    } catch { notify('تعذر تنزيل النموذج'); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (importSource === 'tayyar') {
      setImportState({ busy: true });
      const fd = new FormData(); fd.append('file', file);
      try {
        const res = await axios.post(`${API}/players/excel-preview`, fd, auth());
        setImportState({ preview: res.data, resolutions: buildDefaultCategoryResolutions(res.data.suggested_new_categories) });
      } catch (err) { setImportState({ error: errMsg(err, 'تعذر قراءة الملف') }); }
      return;
    }

    if (importSource === 'rakaz') {
      setRakazState({ busy: true });
      const fd = new FormData(); fd.append('file', file); fd.append('force', 'true');
      try {
        const res = await axios.post(`${API}/players/rakaz-import/preview`, fd, auth());
        setRakazState({ preview: res.data, resolutions: buildDefaultCategoryResolutions(res.data.suggested_new_categories), excluded: new Set() });
      } catch (err) { setRakazState({ error: errMsg(err, 'تعذر قراءة الملف') }); }
      return;
    }

    if (importSource === 'academy') {
      await runAcademyPreview(file);
      return;
    }

    // اكتشاف تلقائي: نحاول ركاز أولاً، وإن لم يُكتشف نُكمل بمستورد تسيار الحالي بلا أي تغيير في منطقه
    setRakazState({ busy: true });
    const fdAuto = new FormData(); fdAuto.append('file', file);
    try {
      const res = await axios.post(`${API}/players/rakaz-import/preview`, fdAuto, auth());
      if (res.data?.detected) {
        setRakazState({ preview: res.data, resolutions: buildDefaultCategoryResolutions(res.data.suggested_new_categories), excluded: new Set() });
        return;
      }
    } catch (err) { setRakazState({ error: errMsg(err, 'تعذر قراءة الملف') }); return; }

    setRakazState(null);
    // كشوف الأكاديميات متعددة الأوراق — تُكتشف قبل قالب تسيار
    const academyDetected = await runAcademyPreview(file, true);
    if (academyDetected) return;

    setImportState({ busy: true });
    const fdTayyar = new FormData(); fdTayyar.append('file', file);
    try {
      const res = await axios.post(`${API}/players/excel-preview`, fdTayyar, auth());
      setImportState({ preview: res.data, resolutions: buildDefaultCategoryResolutions(res.data.suggested_new_categories) });
    } catch (err) { setImportState({ error: errMsg(err, 'تعذر قراءة الملف') }); }
  };

  const runAcademyPreview = async (file, silentIfUndetected) => {
    setAcademyState({ busy: true });
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/players/academy-import/preview`, fd, auth());
      if (!res.data?.detected && silentIfUndetected) { setAcademyState(null); return false; }
      setAcademyState({
        preview: res.data,
        resolutions: buildDefaultCategoryResolutions(res.data.suggested_new_categories),
        excluded: new Set(), recordPayments: true, endSource: 'file',
      });
      return true;
    } catch (err) {
      if (silentIfUndetected) { setAcademyState(null); return false; }
      setAcademyState({ error: errMsg(err, 'تعذر قراءة الملف') });
      return true;
    }
  };

  const confirmAcademyImport = async () => {
    setAcademyState((s) => ({ ...s, busy: true }));
    try {
      const { preview, resolutions, excluded, recordPayments, endSource } = academyState;
      const res = await axios.post(`${API}/players/academy-import/confirm`, {
        sheets: preview.sheets.filter((sh) => sh.detected).map((sh) => ({
          sheet: sh.sheet, category_text: sh.category_text, matched_category_id: sh.matched_category_id, players: sh.players,
        })),
        category_resolutions: resolutions,
        excluded_keys: Array.from(excluded),
        record_payments: Boolean(recordPayments),
        end_source: endSource === 'computed' ? 'computed' : 'file',
      }, auth());
      setAcademyState(null);
      const d = res.data || {};
      setLastImport(d.batch_id ? { ...d } : null);
      notify(`تم استيراد ${d.imported_count} لاعباً وتحديث ${d.updated_count}${d.payments_recorded ? ` وتسجيل ${d.payments_recorded} دفعة بإجمالي ${d.payments_total} ر.س` : ''}`);
      load();
    } catch (err) { setAcademyState((s) => ({ ...s, busy: false, error: errMsg(err, 'فشل الاستيراد') })); }
  };

  const undoLastImport = async () => {
    if (!lastImport?.batch_id) return;
    setUndoBusy(true);
    try {
      const res = await axios.post(`${API}/players/import-batches/${lastImport.batch_id}/undo`, {}, auth());
      const d = res.data || {};
      setLastImport(null);
      notify(`تم التراجع — حُذف ${d.deleted_players} لاعباً و${d.deleted_payments} دفعة، وأُعيد ${d.restored_players} لاعباً لحالتهم السابقة`);
      load();
    } catch (err) { notify(errMsg(err, 'تعذّر التراجع عن الاستيراد')); }
    setUndoBusy(false);
  };

  const confirmRakazImport = async () => {    setRakazState((s) => ({ ...s, busy: true }));
    try {
      const { preview, resolutions, excluded } = rakazState;
      const res = await axios.post(`${API}/players/rakaz-import/confirm`, {
        new_players: preview.new_players,
        existing_players: preview.existing_players,
        category_resolutions: resolutions,
        excluded_temp_ids: Array.from(excluded),
        packages_to_create: preview.packages_to_create || [],
      }, auth());
      setRakazState(null);
      notify(`تم استيراد ${res.data.imported_count} وتحديث ${res.data.updated_count} لاعباً من ركاز`);
      load();
    } catch (err) { setRakazState((s) => ({ ...s, busy: false, error: errMsg(err, 'فشل الاستيراد') })); }
  };

  const confirmImport = async () => {
    setImportState((s) => ({ ...s, busy: true }));
    try {
      const res = await axios.post(`${API}/players/excel-import`, { ...importState.preview, category_resolutions: importState.resolutions }, auth());
      setImportState(null); notify(`تم استيراد ${res.data?.imported_count ?? ''} لاعباً`); load();
    } catch (err) { setImportState((s) => ({ ...s, busy: false, error: errMsg(err, 'فشل الاستيراد') })); }
  };

  const resetFilters = () => { setSearch(''); setCatFilter(''); setPosFilter(''); setSubFilter(''); setPaymentFilter(''); setActiveFilter('active'); setPage(1); };

  const applyKpi = (key) => {
    setPage(1);
    if (key === 'total') { setView('players'); setActiveFilter('all'); setSubFilter(''); }
    else if (key === 'active') { setView('players'); setActiveFilter('active'); setSubFilter(''); }
    else if (key === 'sub_active') { setView('subscriptions'); setSubFilter('active'); }
    else if (key === 'sub_pending') { setView('subscriptions'); setSubFilter('pending_payment'); }
    else if (key === 'sub_expiring') { setView('subscriptions'); setSubFilter('expiring_30'); }
    else if (key === 'sub_expired') { setView('subscriptions'); setSubFilter('expired'); }
  };

  const onAlertClick = () => { setView('subscriptions'); setSubFilter('expiring_7'); setPage(1); };

  const pool = players.filter((p) => !p.is_archived);
  const active = pool.filter((p) => p.is_active);
  const registered = pool.filter((p) => subscriptionBadge(p).key !== 'not_registered');
  const subActiveList = registered.filter((p) => subscriptionBadge(p).key === 'active');
  const subPendingList = registered.filter((p) => subscriptionBadge(p).key === 'pending_payment');
  const subExpiredList = registered.filter((p) => subscriptionBadge(p).key === 'expired');
  const subExpiringSoonList = registered.filter((p) => isExpiringWithin(p, 30));
  const subExpiring7List = registered.filter((p) => isExpiringWithin(p, 7));

  const metrics = {
    total: pool.length, active: active.length, sub_active: subActiveList.length,
    sub_pending: subPendingList.length, sub_expiring: subExpiringSoonList.length, sub_expired: subExpiredList.length,
  };

  const filtered = players.filter((p) => {
    if (activeFilter === 'archived') { if (!p.is_archived) return false; }
    else if (p.is_archived) return false;
    else if (activeFilter === 'active' && !p.is_active) return false;
    else if (activeFilter === 'inactive' && p.is_active) return false;
    if (catFilter && p.category_id !== catFilter) return false;
    if (posFilter && p.position !== posFilter) return false;
    if (subFilter) {
      if (subFilter === 'expiring_30') { if (!isExpiringWithin(p, 30)) return false; }
      else if (subFilter === 'expiring_7') { if (!isExpiringWithin(p, 7)) return false; }
      else if (subscriptionBadge(p).key !== subFilter) return false;
    }
    if (paymentFilter && p.subscription_payment_status !== paymentFilter) return false;
    const q = search.trim();
    if (q && !(p.name?.includes(q) || String(p.jersey_number || '').includes(q) || (p.phone || '').includes(q) || (p.guardian_phone || '').includes(q))) return false;
    return true;
  });
  const paged = filtered.slice((page - 1) * 15, page * 15);

  return (
    <div className="mx-auto max-w-7xl space-y-4" data-testid="players-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title="اللاعبون" desc="الإدارة الرياضية والاشتراكات المالية للاعب في مكان واحد"
        action={
          <div className="flex flex-wrap gap-2">
            <button onClick={downloadTemplate} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-teal-300" data-testid="players-template-btn"><Icon name="document" className="w-4 h-4" /> نموذج Excel</button>
            <select value={importSource} onChange={(e) => setImportSource(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm text-slate-700" data-testid="players-import-source-select">
              <option value="auto">اكتشاف تلقائي</option>
              <option value="tayyar">قالب تسيار</option>
              <option value="rakaz">تصدير اشتراكات ركاز</option>
              <option value="academy">كشف استحقاق أكاديمية (عدة أوراق)</option>
            </select>
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-teal-300" data-testid="players-import-btn"><Icon name="arrowUp" className="w-4 h-4" /> استيراد</button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="players-import-input" />
            <PrimaryBtn onClick={() => openForm(null)} data-testid="player-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة لاعب</PrimaryBtn>
          </div>
        } />

      {lastImport?.batch_id && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="last-import-banner">
          <div className="text-sm text-emerald-800">
            <b>تم الاستيراد بنجاح</b> — {lastImport.imported_count} لاعب جديد · {lastImport.updated_count} محدَّث
            {lastImport.payments_recorded ? ` · ${lastImport.payments_recorded} دفعة بإجمالي ${lastImport.payments_total} ر.س` : ''}
            <span className="mt-0.5 block text-xs text-emerald-700/80">إن كانت النتيجة غير متوقعة يمكنك التراجع الكامل الآن — يحذف ما أُنشئ ويعيد المحدَّثين لحالتهم السابقة.</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setLastImport(null)} className="rounded-lg px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100" data-testid="last-import-dismiss-btn">إخفاء</button>
            <button onClick={undoLastImport} disabled={undoBusy} className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-50" data-testid="undo-import-btn">
              {undoBusy ? 'جارٍ التراجع...' : 'تراجع عن هذا الاستيراد'}
            </button>
          </div>
        </div>
      )}

      <PlayerKpiBar metrics={metrics} onSelect={applyKpi} />      <ExpiringAlert count={subExpiring7List.length} onClick={onAlertClick} />

      <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1" data-testid="players-view-toggle">
        <button onClick={() => setView('players')} className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${view === 'players' ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="players-view-btn">اللاعبون</button>
        <button onClick={() => setView('subscriptions')} className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${view === 'subscriptions' ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="subscriptions-view-btn">الاشتراكات</button>
      </div>

      {view === 'players' && (
        <BulkToolbar
          count={selected.size}
          categories={categories}
          onAssignCategory={bulkAssignCategory}
          onActivate={bulkActivate}
          onDeactivate={bulkDeactivate}
          onArchive={bulkArchive}
          onExport={bulkExport}
          onPrintCards={() => setBulkCardsPlayers(selectedPlayers)}
          onClear={clearSelection}
        />
      )}

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
            <Icon name="search" className="w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="بحث بالاسم أو الجوال أو رقم اللاعب..." className="w-52 bg-transparent text-sm outline-none" data-testid="player-search" />
          </div>
          <Select value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setPage(1); }} testId="player-filter-category">
            <option value="">كل الفئات</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select value={posFilter} onChange={(e) => { setPosFilter(e.target.value); setPage(1); }} testId="player-filter-position">
            <option value="">كل المراكز</option>
            {Object.entries(POSITIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Select value={subFilter} onChange={(e) => { setSubFilter(e.target.value); setPage(1); }} testId="player-filter-subscription">
            <option value="">كل حالات الاشتراك</option>
            {Object.entries(SUB_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            <option value="expiring_30">{EXPIRING_LABELS.expiring_30}</option>
            <option value="expiring_7">{EXPIRING_LABELS.expiring_7}</option>
            <option value="not_registered">غير مسجل</option>
          </Select>
          <Select value={paymentFilter} onChange={(e) => { setPaymentFilter(e.target.value); setPage(1); }} testId="player-filter-payment">
            <option value="">كل حالات الدفع</option>
            {Object.entries(PAYMENT_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }} testId="player-filter-active">
            <option value="active">النشطون</option>
            <option value="inactive">المعطلون</option>
            <option value="archived">المؤرشفون</option>
            <option value="all">الكل</option>
          </Select>
          {EXPIRING_LABELS[subFilter] && (
            <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700" data-testid="expiring-filter-hint">الفترة المطبقة: {EXPIRING_LABELS[subFilter]}</span>
          )}
          <button onClick={resetFilters} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:border-teal-300" data-testid="players-reset-filters">إعادة ضبط الفلاتر</button>
          <span className="mr-auto text-xs text-slate-400">{filtered.length} {view === 'players' ? 'لاعب' : 'اشتراك'}</span>
        </div>

        {loading ? <div className="space-y-2 p-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : paged.length === 0 ? <div className="p-6"><EmptyState icon="users" text={view === 'players' ? 'لا يوجد لاعبون مطابقون' : 'لا توجد اشتراكات مطابقة'} testId="players-empty" /></div>
          : view === 'players' ? (
            <PlayersTable players={paged} categories={categories} catName={catName} onView={setViewing} onEdit={openForm} onCard={setBarcodePlayer} onToggle={toggleActive} onArchive={toggleArchive} onDelete={setConfirmDel} canDelete={canDelete} canWhatsApp={canWhatsApp} notify={notify} selected={selected} onToggleSelect={toggleSelect} onToggleSelectAll={toggleSelectAll} />
          ) : (
            <SubscriptionsTable players={paged} catName={catName} onView={setViewing} onEdit={openForm} canFinance={canFinance} canWhatsApp={canWhatsApp} onPay={onPay} onRenew={onRenew} onExtend={onExtend} onPauseResume={onPauseResume} onCancel={onCancelSub} onActivate={onActivateSub} />
          )}
        <Paginator page={page} total={filtered.length} limit={15} onChange={setPage} />
      </div>

      {canDeleteAllPlayers && (
        <div className="rounded-xl border border-red-200 bg-white" data-testid="danger-zone-accordion">
          <button
            onClick={() => setDangerZoneAccordionOpen((o) => !o)}
            className="flex w-full items-center justify-between gap-2 px-4 py-3 text-right"
            data-testid="danger-zone-accordion-toggle"
          >
            <span className="text-sm font-bold text-red-700">منطقة الخطر</span>
            <Icon name="chevronDown" className={`w-4 h-4 text-red-600 transition-transform ${dangerZoneAccordionOpen ? 'rotate-180' : ''}`} />
          </button>
          {dangerZoneAccordionOpen && (
            <div className="flex flex-col gap-2 border-t border-red-100 bg-red-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm font-semibold text-red-700">حذف نهائي لكل اللاعبين والاشتراكات في منشأتك</span>
              <button
                onClick={() => setDangerZoneOpen(true)}
                className="flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-bold text-red-600 hover:bg-red-100"
                data-testid="danger-zone-open-btn"
              >
                <Icon name="trash" className="w-4 h-4" /> حذف جميع اللاعبين والاشتراكات
              </button>
            </div>
          )}
        </div>
      )}

      {subAction && (
        <SubscriptionActionModal action={subAction} onClose={() => setSubAction(null)} onDone={() => { setSubAction(null); notify('تم تنفيذ العملية بنجاح'); load(); }} />
      )}

      {dangerZoneOpen && (
        <DangerZoneDeleteAllModal
          onClose={() => setDangerZoneOpen(false)}
          onDeleted={() => { setDangerZoneOpen(false); notify('تم حذف جميع اللاعبين والاشتراكات نهائياً'); setSelected(new Set()); load(); }}
        />
      )}
      {bulkCardsPlayers && <BulkCardsModal players={bulkCardsPlayers} club={club} catName={catName} onClose={() => setBulkCardsPlayers(null)} />}

      {modal && (
      <div className="fixed left-0 top-0 z-50 flex h-screen w-screen items-end justify-center sm:items-center sm:p-4" data-testid="player-modal">
        <div className="absolute left-0 top-0 h-full w-full bg-slate-900/55 backdrop-blur-[3px]" onClick={() => setModal(false)} />
        <div className="relative flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-[0_30px_80px_-20px_rgba(10,20,35,0.6)] sm:max-h-[92vh] sm:rounded-3xl">
          <div className="relative shrink-0 overflow-hidden bg-[#0f1726] px-5 py-4 sm:px-6 sm:py-5">
            <span className="pointer-events-none absolute -right-12 -top-16 h-44 w-44 rounded-full bg-emerald-500/20 blur-3xl" />
            <span className="pointer-events-none absolute -left-8 bottom-0 h-28 w-28 rounded-full bg-teal-400/10 blur-2xl" />
            <div className="relative flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-400 to-emerald-600 text-white shadow-lg shadow-emerald-500/25">
                <Icon name="users" className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-extrabold text-white sm:text-lg">{editing ? 'تعديل بيانات اللاعب' : 'إضافة لاعب جديد'}</h3>
                <p className="mt-0.5 text-[11px] leading-relaxed text-white/55">
                  {editing ? 'حدّث بيانات اللاعب واشتراكه ثم احفظ' : 'ثلاث خطوات سريعة: البيانات الشخصية، ثم الفئة والبيانات الرياضية، ثم الاشتراك'}
                </p>
              </div>
              <button type="button" onClick={() => setModal(false)} title="إغلاق" data-testid="player-modal-close-btn"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-white/80 transition-all hover:bg-white/20 active:scale-90">
                <Icon name="x" className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
        {formError && <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-700" data-testid="player-form-error"><Icon name="warning" className="mt-0.5 h-4 w-4 shrink-0" />{formError}</div>}
        {(() => {
          const steps = canFinance ? FORM_STEPS : FORM_STEPS.slice(0, 2);
          const totalSteps = steps.length;
          const curStep = Math.min(formStep, totalSteps);
          const age = computeAgeFromBirth(form.birth_date);
          const suggested = suggestCategory(age, categories);
          const amountNum = form.subscription_amount !== '' && form.subscription_amount != null ? Number(form.subscription_amount) : null;
          const paidNum = form.subscription_paid_amount !== '' && form.subscription_paid_amount != null ? Number(form.subscription_paid_amount) : null;
          const autoPaymentStatus = !amountNum ? null : (paidNum >= amountNum ? 'paid' : paidNum > 0 ? 'partial' : 'unpaid');
          const goNext = () => {
            if (curStep === 1 && !form.name?.trim()) { setFormError('اسم اللاعب مطلوب'); return; }
            if (curStep === 2 && !form.category_id) { setFormError('الفئة حقل مطلوب'); return; }
            setFormError(null);
            setFormStep(curStep + 1);
          };
          const insetPct = 50 / totalSteps;
          return (
            <div className="space-y-6" data-testid="player-form-wizard">
              <div className="relative flex justify-between rounded-2xl border border-slate-200/70 bg-slate-50/70 px-2 py-3" data-testid="player-form-progress">
                <div className="absolute top-[26px] h-0.5 bg-slate-200" style={{ left: `${insetPct}%`, right: `${insetPct}%` }} />
                <div
                  className="absolute top-[26px] h-0.5 bg-emerald-500 transition-transform duration-300"
                  style={{ left: `${insetPct}%`, right: `${insetPct}%`, transform: `scaleX(${totalSteps > 1 ? (curStep - 1) / (totalSteps - 1) : 1})`, transformOrigin: 'right' }}
                />
                {steps.map((s, i) => {
                  const idx = i + 1;
                  const isDone = idx < curStep;
                  const isCurrent = idx === curStep;
                  return (
                    <div key={s} className="relative z-10 flex flex-col items-center gap-1.5" style={{ width: `${100 / totalSteps}%` }} data-testid={`player-form-step-indicator-${idx}`}>
                      <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold shadow-sm transition-all ${isDone ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white' : isCurrent ? 'bg-[#0f1726] text-white ring-4 ring-emerald-500/15' : 'border-2 border-slate-200 bg-white text-slate-400'}`}>
                        {isDone ? <Icon name="check" className="h-4 w-4" /> : idx}
                      </div>
                      <span className={`text-center text-[11px] font-semibold leading-tight ${isCurrent ? 'text-[#16233b]' : isDone ? 'text-emerald-600' : 'text-slate-400'}`}>{s}</span>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={(e) => { if (curStep < totalSteps) { e.preventDefault(); goNext(); } else submit(e); }} className="space-y-5">
                {curStep === 1 && (
                  <div className="space-y-5" data-testid="player-form-step-1">
                    <div>
                      <div className="mb-3 flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-50 text-teal-700 ring-1 ring-teal-100"><Icon name="users" className="h-3.5 w-3.5" /></span><span className="text-xs font-extrabold text-slate-700">البيانات الشخصية</span><span className="h-px flex-1 bg-slate-100" /></div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="اسم اللاعب" required><input className={inputCls} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="player-input-name" /></Field>
                        <Field label="تاريخ الميلاد"><PremiumDatePicker value={form.birth_date || ''} onChange={(v) => setForm({ ...form, birth_date: v })} testId="player-input-birth-date" maxDate={new Date().toISOString().slice(0, 10)} /></Field>
                        <Field label="الجنسية"><input className={inputCls} value={form.nationality || ''} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></Field>
                        <Field label="رابط الصورة"><input dir="ltr" placeholder="https://..." className={inputCls} value={form.photo_url || ''} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} /></Field>
                      </div>
                    </div>
                    <div>
                      <div className="mb-3 flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-50 text-teal-700 ring-1 ring-teal-100"><Icon name="inbox" className="h-3.5 w-3.5" /></span><span className="text-xs font-extrabold text-slate-700">بيانات التواصل</span><span className="h-px flex-1 bg-slate-100" /></div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="جوال اللاعب"><input dir="ltr" className={inputCls} value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="player-input-phone" /></Field>
                        <Field label="اسم ولي الأمر"><input className={inputCls} value={form.guardian_name || ''} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} /></Field>
                        <Field label="جوال ولي الأمر"><input dir="ltr" className={inputCls} value={form.guardian_phone || ''} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} data-testid="player-input-guardian-phone" /></Field>
                        <div className="sm:col-span-2">
                          <Field label="عنوان السكن (اختياري)"><input className={inputCls} placeholder="مثال: حي النرجس، شارع الأمير سلطان" value={form.home_address || ''} onChange={(e) => setForm({ ...form, home_address: e.target.value })} data-testid="player-input-home-address" /></Field>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {curStep === 2 && (
                  <div className="space-y-5" data-testid="player-form-step-2">
                    <div>
                      <div className="mb-3 flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-50 text-teal-700 ring-1 ring-teal-100"><Icon name="dumbbell" className="h-3.5 w-3.5" /></span><span className="text-xs font-extrabold text-slate-700">البيانات الرياضية</span><span className="h-px flex-1 bg-slate-100" /></div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="المركز">
                          <select className={inputCls} value={form.position || ''} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="player-input-position">
                            <option value="">غير محدد</option>
                            {Object.entries(POSITIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </Field>
                        <Field label="الطول (سم)"><input type="number" min="0" className={inputCls} value={form.height || ''} onChange={(e) => setForm({ ...form, height: e.target.value })} /></Field>
                        <Field label="الوزن (كجم)"><input type="number" min="0" className={inputCls} value={form.weight || ''} onChange={(e) => setForm({ ...form, weight: e.target.value })} /></Field>
                        <Field label="القدم المفضلة">
                          <select className={inputCls} value={form.preferred_foot || ''} onChange={(e) => setForm({ ...form, preferred_foot: e.target.value })}>
                            <option value="">غير محدد</option>
                            {Object.entries(FEET).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </Field>
                      </div>
                    </div>
                    <div>
                      <div className="mb-3 flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-50 text-teal-700 ring-1 ring-teal-100"><Icon name="userGroup" className="h-3.5 w-3.5" /></span><span className="text-xs font-extrabold text-slate-700">الفريق والفئة</span><span className="h-px flex-1 bg-slate-100" /></div>
                      {form.birth_date && (
                        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs" data-testid="player-age-suggestion">
                          <span className="font-semibold text-slate-600">العمر المحسوب: {age ?? '—'} سنة</span>
                          {suggested && (
                            <span className="flex items-center gap-1.5 text-teal-700">
                              — الفئة المقترحة: <b>{suggested.name}</b>
                              {form.category_id !== suggested.id && (
                                <button type="button" onClick={() => setForm({ ...form, category_id: suggested.id })} className="rounded-full bg-teal-50 px-2 py-0.5 font-semibold text-teal-700 hover:bg-teal-100" data-testid="player-use-suggested-category">استخدام المقترحة</button>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <Field label="الفئة" required>
                            <div className="flex gap-2">
                              <select className={inputCls} value={form.category_id || ''} onChange={(e) => setForm({ ...form, category_id: e.target.value })} data-testid="player-input-category">
                                <option value="">اختر الفئة</option>
                                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}{c.min_age && c.max_age ? ` (${c.min_age}-${c.max_age} سنة)` : ''}</option>)}
                              </select>
                              <button
                                type="button"
                                onClick={() => setQuickCat(quickCat ? null : { name: '', min_age: '', max_age: '' })}
                                title="إضافة فئة جديدة"
                                data-testid="player-quick-add-category-btn"
                                className={`flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-lg border text-lg font-bold transition-all active:scale-90 ${quickCat ? 'border-teal-400 bg-teal-50 text-teal-700' : 'border-slate-300 bg-white text-slate-500 hover:border-teal-400 hover:text-teal-700'}`}
                              >
                                {quickCat ? '×' : '+'}
                              </button>
                            </div>
                          </Field>
                          <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500" data-testid="player-category-hint">
                            الفئة هي المجموعة السنية التي يتدرب فيها اللاعب (مثل: براعم، تحت 12، تحت 15، الفريق الأول). تُستخدم لتوزيع التدريبات والفرق والباقات والتقارير. اضغط <b>+</b> لإضافة فئة جديدة من هنا مباشرة.
                          </p>
                          {quickCat && (
                            <div className="mt-2 rounded-xl border border-teal-200 bg-teal-50/60 p-3" data-testid="player-quick-category-panel">
                              <div className="mb-2 text-[11px] font-bold text-teal-800">فئة جديدة — ستُضاف تلقائياً إلى الفرق والفئات</div>
                              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                <input className={inputCls} placeholder="اسم الفئة (تحت 15)" value={quickCat.name} onChange={(e) => setQuickCat({ ...quickCat, name: e.target.value })} data-testid="player-quick-category-name" />
                                <input type="number" min="4" max="60" className={inputCls} placeholder="العمر من" value={quickCat.min_age} onChange={(e) => setQuickCat({ ...quickCat, min_age: e.target.value })} data-testid="player-quick-category-min" />
                                <input type="number" min="4" max="60" className={inputCls} placeholder="العمر إلى" value={quickCat.max_age} onChange={(e) => setQuickCat({ ...quickCat, max_age: e.target.value })} data-testid="player-quick-category-max" />
                              </div>
                              {quickCat.error && <div className="mt-2 text-[11px] font-bold text-red-600" data-testid="player-quick-category-error">{quickCat.error}</div>}
                              <div className="mt-2.5 flex gap-2">
                                <button type="button" onClick={submitQuickCategory} disabled={quickCat.busy} className="rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-teal-500/20 transition-all active:scale-95 disabled:opacity-50" data-testid="player-quick-category-save">
                                  {quickCat.busy ? 'جارٍ الإضافة...' : 'إضافة الفئة'}
                                </button>
                                <button type="button" onClick={() => setQuickCat(null)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-600">إلغاء</button>
                              </div>
                            </div>
                          )}
                        </div>
                        <Field label="رقم القميص (تلقائي إذا تُرك فارغاً)"><input type="number" min="1" className={inputCls} value={form.jersey_number || ''} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} data-testid="player-input-jersey" /></Field>
                      </div>
                    </div>
                  </div>
                )}

                {curStep === 3 && canFinance && (
                  <div data-testid="player-form-step-3">
                    <div className="mb-3 flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-lg bg-teal-50 text-teal-700 ring-1 ring-teal-100"><Icon name="wallet" className="h-3.5 w-3.5" /></span><span className="text-xs font-extrabold text-slate-700">الاشتراك والدفع (اختياري)</span><span className="h-px flex-1 bg-slate-100" /></div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="الباقة">
                        <select className={inputCls} value={form.subscription_package_id || CUSTOM_PACKAGE_VALUE} onChange={(e) => onPackageSelect(e.target.value)} data-testid="player-input-sub-package-select">
                          <option value={CUSTOM_PACKAGE_VALUE}>اشتراك مخصص (إدخال يدوي)</option>
                          {packages.filter((pk) => !pk.category_ids?.length || pk.category_ids.includes(form.category_id)).map((pk) => (
                            <option key={pk.id} value={pk.id}>{pk.name} — {pk.price} ر.س / {pk.duration_value} {DURATION_UNIT_LABELS[pk.duration_unit]}</option>
                          ))}
                        </select>
                      </Field>
                      <Field label="اسم/نوع الباقة"><input className={inputCls} value={form.subscription_package || ''} onChange={(e) => setForm({ ...form, subscription_package: e.target.value })} data-testid="player-input-sub-package" /></Field>
                      {(() => {
                        const pkg = packages.find((x) => x.id === form.subscription_package_id);
                        const mode = pkg?.sessions_mode || 'unlimited';
                        if (!pkg || mode === 'unlimited') return null;
                        const canOverride = pkg.allow_player_override !== false;
                        const isWeekly = mode === 'weekly';
                        const base = isWeekly ? pkg.sessions_per_week : pkg.total_sessions;
                        const field = isWeekly ? 'sessions_per_week' : 'sessions_total_override';
                        const custom = form[field] !== '' && form[field] !== null && form[field] !== undefined;
                        return (
                          <div className="sm:col-span-2 rounded-xl border border-sky-200 bg-sky-50/60 p-3" data-testid="player-sessions-block">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="text-xs font-extrabold text-sky-900">
                                الحصص التدريبية — الباقة: {base} {isWeekly ? 'حصص في الأسبوع' : 'حصة إجمالاً'}
                              </span>
                              {canOverride && (
                                <button
                                  type="button"
                                  onClick={() => setForm({ ...form, [field]: custom ? '' : base })}
                                  data-testid="player-sessions-override-toggle"
                                  className={`rounded-lg px-3 py-1.5 text-[11px] font-bold transition-all active:scale-95 ${custom ? 'bg-sky-600 text-white' : 'border border-sky-300 bg-white text-sky-700 hover:border-sky-500'}`}
                                >
                                  {custom ? 'إلغاء التخصيص (العودة لقيمة الباقة)' : 'تخصيص عدد مختلف لهذا اللاعب'}
                                </button>
                              )}
                            </div>
                            {custom && (
                              <div className="mt-2.5 flex items-end gap-2">
                                <div className="w-40">
                                  <Field label={isWeekly ? 'حصص هذا اللاعب في الأسبوع' : 'رصيد حصص هذا اللاعب'}>
                                    <input type="number" min="1" max={isWeekly ? 14 : 500} className={inputCls} value={form[field]}
                                      onChange={(e) => setForm({ ...form, [field]: e.target.value })} data-testid="player-input-sessions-value" />
                                  </Field>
                                </div>
                                <span className="pb-3 text-[11px] font-semibold text-sky-700">بدل {base} في الباقة</span>
                              </div>
                            )}
                            {!canOverride && <p className="mt-1.5 text-[11px] text-sky-800/70">هذه الباقة لا تسمح بتخصيص عدد حصص مختلف لكل لاعب.</p>}
                          </div>
                        );
                      })()}
                      <Field label="حالة الاشتراك">
                        <select className={inputCls} value={form.subscription_status || ''} onChange={(e) => setForm({ ...form, subscription_status: e.target.value })} data-testid="player-input-sub-status">
                          <option value="">غير محدد</option>
                          {Object.entries(SUB_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </Field>
                      <Field label="بداية الاشتراك"><PremiumDatePicker value={form.subscription_start || ''} onChange={(v) => setForm({ ...form, subscription_start: v })} testId="player-input-sub-start" /></Field>
                      <Field label="نهاية الاشتراك"><PremiumDatePicker value={form.subscription_end || ''} onChange={(v) => setForm({ ...form, subscription_end: v })} testId="player-input-sub-end" /></Field>
                      <Field label="قيمة الاشتراك (ر.س)"><input type="number" min="0" className={inputCls} value={form.subscription_amount ?? ''} onChange={(e) => setForm({ ...form, subscription_amount: e.target.value })} data-testid="player-input-sub-amount" /></Field>
                    </div>

                    <div className="mt-4 rounded-lg border border-slate-100 p-3">
                      <div className="mb-2 text-xs font-bold text-slate-400">الخصم</div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <Field label="نوع الخصم">
                          <select className={inputCls} value={form.subscription_discount_type || ''} onChange={(e) => onDiscountChange({ subscription_discount_type: e.target.value || '' })} data-testid="player-input-discount-type">
                            <option value="">بدون خصم</option>
                            <option value="amount">مبلغ ثابت</option>
                            <option value="percent">نسبة %</option>
                          </select>
                        </Field>
                        <Field label="قيمة الخصم">
                          <input type="number" min="0" max={form.subscription_discount_type === 'percent' ? 100 : undefined} className={inputCls} value={form.subscription_discount_value ?? ''} onChange={(e) => onDiscountChange({ subscription_discount_value: e.target.value })} disabled={!form.subscription_discount_type} data-testid="player-input-discount-value" />
                        </Field>
                        <Field label="سبب الخصم (اختياري)"><input className={inputCls} value={form.subscription_discount_reason || ''} onChange={(e) => setForm({ ...form, subscription_discount_reason: e.target.value })} disabled={!form.subscription_discount_type} data-testid="player-input-discount-reason" /></Field>
                      </div>
                      {form.subscription_discount_type && <div className="mt-2 text-xs text-slate-500">السعر النهائي بعد الخصم: <span className="font-bold text-teal-700" data-testid="player-final-amount-display">{form.subscription_amount || 0} ر.س</span></div>}
                    </div>

                    <div className="mt-4 rounded-lg border border-slate-100 p-3">
                      <div className="mb-2 text-xs font-bold text-slate-400">الدفع</div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Field label="المبلغ المدفوع (ر.س)"><input type="number" min="0" className={inputCls} value={form.subscription_paid_amount ?? ''} onChange={(e) => setForm({ ...form, subscription_paid_amount: e.target.value })} data-testid="player-input-sub-paid" /></Field>
                        <Field label="حالة الدفع (محسوبة تلقائياً)">
                          <div className={`flex h-[42px] items-center rounded-lg border border-slate-200 px-3 text-sm font-semibold ${autoPaymentStatus === 'paid' ? 'text-emerald-600' : autoPaymentStatus === 'partial' ? 'text-amber-600' : autoPaymentStatus === 'unpaid' ? 'text-red-600' : 'text-slate-400'}`} data-testid="player-input-sub-payment-status">
                            {autoPaymentStatus ? PAYMENT_STATUS_LABELS[autoPaymentStatus] : 'يُحسب بعد إدخال القيمة'}
                          </div>
                        </Field>
                        <Field label="طريقة الدفع">
                          <select className={inputCls} value={form.subscription_payment_method || ''} onChange={(e) => setForm({ ...form, subscription_payment_method: e.target.value })} data-testid="player-input-payment-method">
                            <option value="">غير محدد</option>
                            {Object.entries(PLAYER_PAYMENT_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </Field>
                        <Field label="مرجع الدفع (اختياري)"><input className={inputCls} value={form.subscription_payment_reference || ''} onChange={(e) => setForm({ ...form, subscription_payment_reference: e.target.value })} data-testid="player-input-payment-reference" /></Field>
                      </div>
                      {editing && (
                        <div className="mt-3">
                          <Field label="إيصال الدفع (اختياري — PDF/JPG/PNG)">
                            <div className="flex items-center gap-2">
                              <label className="cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-teal-300">
                                رفع إيصال
                                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={uploadSubscriptionReceipt} data-testid="player-input-receipt-upload" />
                              </label>
                              {editing.subscription_receipt_filename && <span className="text-xs text-slate-500">{editing.subscription_receipt_filename}</span>}
                            </div>
                          </Field>
                        </div>
                      )}
                    </div>

                    <Field label="ملاحظات الاشتراك"><input className={inputCls} value={form.subscription_notes || ''} onChange={(e) => setForm({ ...form, subscription_notes: e.target.value })} /></Field>
                  </div>
                )}

                <div className="sticky bottom-0 -mx-5 flex items-center justify-between gap-2 border-t border-slate-200/80 bg-white/95 px-5 py-3 backdrop-blur-md sm:-mx-6 sm:px-6">
                  <button type="button" onClick={() => setFormStep((s) => Math.max(1, s - 1))} disabled={curStep === 1} className="rounded-xl px-3 py-2.5 text-sm font-bold text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-0" data-testid="player-form-prev">السابق</button>
                  <div className="flex items-center gap-2">
                    <span className="me-1 hidden text-[11px] font-bold text-slate-400 sm:inline">الخطوة {curStep} من {totalSteps}</span>
                    <button type="button" onClick={() => setModal(false)} className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:border-slate-400">إلغاء</button>
                    {curStep < totalSteps
                      ? <PrimaryBtn type="submit" data-testid="player-form-next">التالي</PrimaryBtn>
                      : (
                        <>
                          {canFinance && <button type="button" onClick={(e) => submit(e, true)} className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:border-teal-400 hover:text-teal-700" data-testid="player-form-save-no-sub">حفظ دون اشتراك</button>}
                          <PrimaryBtn type="submit" data-testid="player-form-submit">حفظ</PrimaryBtn>
                        </>
                      )}
                  </div>
                </div>
              </form>
            </div>
          );
        })()}
          </div>
        </div>
      </div>
      )}

      <Modal open={Boolean(viewing)} title="ملف اللاعب" onClose={() => { setViewing(null); setShowSubHistory(false); }} wide>
        {viewing && (
          <div className="space-y-4" data-testid="player-profile">
            <div className="flex items-center gap-4">
              <Avatar p={viewing} />
              <div>
                <div className="text-lg font-bold text-slate-900">{viewing.name}</div>
                <div className="text-sm text-slate-500">{POSITIONS[viewing.position] || 'مركز غير محدد'} · {catName(viewing.category_id)} · رقم {viewing.jersey_number ?? '—'}</div>
              </div>
              <span className={`mr-auto rounded-full px-2 py-0.5 text-xs font-bold ${viewing.is_active ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{viewing.is_active ? 'نشط' : 'معطل'}</span>
            </div>
            {sessionsUsage && sessionsUsage.sessions_mode !== 'unlimited' && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3" data-testid="player-profile-sessions">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-extrabold text-sky-900">الحصص التدريبية</span>
                  {sessionsUsage.is_custom && <span className="rounded-full bg-sky-600 px-2 py-0.5 text-[10.5px] font-bold text-white">مخصص لهذا اللاعب</span>}
                  {sessionsUsage.package_name && <span className="text-sky-800/70">باقة: {sessionsUsage.package_name}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-bold">
                  {sessionsUsage.sessions_per_week && (
                    <>
                      <span className="rounded-lg bg-white px-2.5 py-1.5 text-sky-800 ring-1 ring-sky-200">{sessionsUsage.sessions_per_week} حصص/أسبوع</span>
                      <span className="rounded-lg bg-white px-2.5 py-1.5 text-slate-700 ring-1 ring-slate-200">حضر {sessionsUsage.used_this_week} هذا الأسبوع</span>
                      <span className={`rounded-lg px-2.5 py-1.5 ring-1 ${sessionsUsage.remaining_this_week === 0 ? 'bg-amber-50 text-amber-700 ring-amber-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200'}`}>
                        {sessionsUsage.remaining_this_week === 0 ? 'استنفد حصص الأسبوع' : `تبقّى ${sessionsUsage.remaining_this_week} حصة`}
                      </span>
                    </>
                  )}
                  {sessionsUsage.total_sessions && (
                    <>
                      <span className="rounded-lg bg-white px-2.5 py-1.5 text-indigo-800 ring-1 ring-indigo-200">رصيد {sessionsUsage.total_sessions} حصة</span>
                      <span className="rounded-lg bg-white px-2.5 py-1.5 text-slate-700 ring-1 ring-slate-200">استُهلك {sessionsUsage.used_in_period}</span>
                      <span className={`rounded-lg px-2.5 py-1.5 ring-1 ${sessionsUsage.remaining_total === 0 ? 'bg-amber-50 text-amber-700 ring-amber-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200'}`}>
                        {sessionsUsage.remaining_total === 0 ? 'نفد الرصيد' : `متبقٍ ${sessionsUsage.remaining_total}`}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[['الأهداف', viewing.total_goals || 0], ['المباريات', viewing.matches_played || 0], ['بطاقات صفراء', viewing.total_yellow_cards || 0], ['بطاقات حمراء', viewing.total_red_cards || 0]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-slate-50 p-3 text-center"><div className="text-lg font-bold text-slate-900">{v}</div><div className="text-xs text-slate-500">{l}</div></div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
              {[['تاريخ الميلاد', viewing.birth_date ? fmtDate(viewing.birth_date) : '—'], ['العمر', viewing.age ?? '—'], ['الجنسية', viewing.nationality || '—'], ['الطول', viewing.height ? `${viewing.height} سم` : '—'], ['الوزن', viewing.weight ? `${viewing.weight} كجم` : '—'], ['القدم المفضلة', FEET[viewing.preferred_foot] || '—'], ['جوال اللاعب', viewing.phone || '—'], ['ولي الأمر', viewing.guardian_name || '—'], ['جوال ولي الأمر', viewing.guardian_phone || '—']].map(([l, v]) => (
                <div key={l}><span className="text-xs text-slate-400">{l}: </span><span className="font-semibold text-slate-700">{v}</span></div>
              ))}
              <div className="col-span-2 sm:col-span-3" data-testid="player-view-home-address"><span className="text-xs text-slate-400">عنوان السكن: </span><span className="font-semibold text-slate-700">{viewing.home_address || '—'}</span></div>
            </div>
            <div className="rounded-lg border border-slate-100 p-3">
              <div className="mb-2 text-xs font-bold text-slate-400">تفاصيل الاشتراك</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                {[['الحالة', subscriptionBadge(viewing).label], ['الباقة', viewing.subscription_package || '—'], ['البداية', viewing.subscription_start ? fmtDate(viewing.subscription_start) : '—'], ['النهاية', viewing.subscription_end ? fmtDate(viewing.subscription_end) : '—'], ['القيمة', viewing.subscription_amount != null ? `${viewing.subscription_amount} ر.س` : '—'], ['المدفوع', viewing.subscription_paid_amount != null ? `${viewing.subscription_paid_amount} ر.س` : '—'], ['حالة الدفع', viewing.subscription_payment_status ? PAYMENT_STATUS_LABELS[viewing.subscription_payment_status] : '—']].map(([l, v]) => (
                  <div key={l}><span className="text-xs text-slate-400">{l}: </span><span className="font-semibold text-slate-700">{v}</span></div>
                ))}
              </div>
              {viewing.subscription_notes && <div className="mt-2 text-xs text-slate-500">ملاحظات: {viewing.subscription_notes}</div>}
              <button
                type="button"
                onClick={() => setShowSubHistory((s) => !s)}
                data-testid="toggle-subscription-history-btn"
                className="mt-3 flex items-center gap-1.5 text-xs font-bold text-teal-600 hover:text-teal-700"
              >
                <Icon name="clock" className="h-3.5 w-3.5" /> {showSubHistory ? 'إخفاء سجل الاشتراكات' : 'عرض سجل الاشتراكات الكامل'}
                <Icon name={showSubHistory ? 'arrowDown' : 'arrowLeft'} className="h-3 w-3" />
              </button>
              {showSubHistory && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <SubscriptionHistoryPanel playerId={viewing.id} />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setViewing(null); setBarcodePlayer(viewing); }} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:border-teal-300" data-testid="profile-barcode-btn">بطاقة اللاعب و QR</button>
              {canWhatsApp && (
                <button onClick={() => setWaProfilePlayer(viewing)} className="flex items-center gap-1.5 rounded-lg border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700 hover:border-emerald-400" data-testid="profile-whatsapp-btn">
                  <Icon name="whatsapp" className="w-4 h-4" /> مراسلة عبر واتساب
                </button>
              )}
              <button onClick={() => inviteGuardian(viewing)} disabled={inviteLoading} className="rounded-lg border border-teal-200 px-4 py-2 text-sm text-teal-700 hover:border-teal-400 disabled:opacity-50" data-testid="profile-invite-guardian-btn">
                {inviteLoading ? 'جارٍ التوليد...' : 'دعوة ولي الأمر'}
              </button>
              <button onClick={() => { setViewing(null); openForm(viewing); }} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:border-teal-300">تعديل البيانات</button>
            </div>
          </div>
        )}
      </Modal>

      {waProfilePlayer && (
        <WhatsAppMessageModal
          open={Boolean(waProfilePlayer)}
          onClose={() => setWaProfilePlayer(null)}
          recipient={{ id: waProfilePlayer.id, name: waProfilePlayer.guardian_name || waProfilePlayer.name, phone: waProfilePlayer.guardian_phone || waProfilePlayer.phone, type: 'player_guardian' }}
          variables={{
            guardian_name: waProfilePlayer.guardian_name || 'ولي الأمر',
            player_name: waProfilePlayer.name,
            subscription_name: waProfilePlayer.subscription_package || '',
            expiry_date: waProfilePlayer.subscription_end ? fmtDate(waProfilePlayer.subscription_end) : '',
            paid_amount: waProfilePlayer.subscription_paid_amount ?? '',
            remaining_amount: (waProfilePlayer.subscription_amount ?? null) !== null && (waProfilePlayer.subscription_paid_amount ?? null) !== null
              ? Math.max(waProfilePlayer.subscription_amount - waProfilePlayer.subscription_paid_amount, 0) : '',
            payment_reference: waProfilePlayer.subscription_payment_reference || '',
          }}
          relatedType="player"
          relatedId={waProfilePlayer.id}
        />
      )}

      <Modal open={Boolean(inviteLink)} title="رابط دعوة ولي الأمر" onClose={() => setInviteLink(null)}>
        {inviteLink && (
          <div className="space-y-3" data-testid="invite-guardian-modal">
            <p className="text-sm text-slate-600">أرسل هذا الرابط لولي أمر «{inviteLink.playerName}» (صالح لمدة 7 أيام). عند استخدامه سيُربط ابنه بحسابه مباشرة بلا حاجة لموافقة إضافية.</p>
            <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-3">
              <input readOnly dir="ltr" value={inviteLink.url} data-testid="invite-guardian-link-input" className="flex-1 bg-transparent text-xs text-slate-600 outline-none" />
              <button onClick={copyInviteLink} data-testid="invite-guardian-copy-btn" className="shrink-0 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-teal-700">نسخ</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف نهائي" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف «{confirmDel?.name}» نهائي ولا يمكن التراجع عنه. يُفضل استخدام الأرشفة أو التعطيل بدلاً من الحذف. متابعة؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="player-confirm-del">حذف نهائي</button>
        </div>
      </Modal>

      {barcodePlayer && <BarcodeCardModal player={barcodePlayer} club={club} catName={catName} onClose={() => setBarcodePlayer(null)} />}

      <Modal open={Boolean(importState)} title="استيراد اللاعبين من Excel" onClose={() => setImportState(null)} wide>
        {importState?.busy && !importState?.preview && <p className="text-sm text-slate-500">جارٍ قراءة الملف...</p>}
        {importState?.error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="import-error">{importState.error}</div>}
        {importState?.preview && (
          <div className="space-y-3" data-testid="import-preview">
            <p className="text-sm text-slate-600">سيتم استيراد <b>{importState.preview.players_preview?.length || 0}</b> لاعباً{importState.preview.errors?.length ? ` — مع ${importState.preview.errors.length} صفوف بها أخطاء سيتم تجاهلها` : ''}.</p>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-100">
              {(importState.preview.players_preview || []).slice(0, 50).map((r, i) => (
                <div key={i} className="flex justify-between border-b border-slate-50 px-3 py-1.5 text-sm"><span className="font-semibold text-slate-700">{r.name}</span><span className="text-slate-500">{r.category_name || ''}{r.pending_category ? ' — بانتظار الفئة' : ''}</span></div>
              ))}
            </div>
            <CategoryResolutionSection
              suggestions={importState.preview.suggested_new_categories}
              resolutions={importState.resolutions}
              onChange={(name, val) => setImportState((s) => ({ ...s, resolutions: { ...s.resolutions, [name]: val } }))}
              categories={categories}
              testidPrefix="tayyar"
            />
            {Boolean(importState.preview.errors?.length) && (
              <div className="max-h-32 overflow-y-auto rounded-lg bg-amber-50 p-2 text-xs text-amber-700" data-testid="import-preview-errors">{importState.preview.errors.slice(0, 20).map((e2, i) => <div key={i}>{typeof e2 === 'string' ? e2 : `الصف ${e2.row ?? '—'}: ${e2.reason || 'خطأ غير معروف'}`}</div>)}</div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setImportState(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
              <PrimaryBtn onClick={confirmImport} disabled={importState.busy || !(importState.preview.players_preview?.length)} data-testid="import-confirm-btn">{importState.busy ? 'جارٍ الاستيراد...' : 'تأكيد الاستيراد'}</PrimaryBtn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(rakazState)} title="استيراد اشتراكات ركاز" onClose={() => setRakazState(null)} wide>
        {rakazState?.busy && !rakazState?.preview && <p className="text-sm text-slate-500">جارٍ قراءة الملف وتحليله...</p>}
        {rakazState?.error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="rakaz-import-error">{rakazState.error}</div>}
        {rakazState?.preview && (() => {
          const { preview } = rakazState;
          const allEntries = [
            ...preview.new_players.map((p) => ({ ...p, _kind: 'new' })),
            ...preview.existing_players.map((p) => ({ ...p, _kind: 'existing' })),
          ];
          return (
            <div className="space-y-4" data-testid="rakaz-import-preview">
              <div className="grid grid-cols-4 gap-2 text-center text-xs">
                <div className="rounded-lg bg-teal-50 p-2" data-testid="rakaz-summary-new"><div className="text-lg font-bold text-teal-700">{preview.summary.new_players}</div>لاعبون جدد</div>
                <div className="rounded-lg bg-blue-50 p-2" data-testid="rakaz-summary-existing"><div className="text-lg font-bold text-blue-700">{preview.summary.existing_players}</div>لاعبون موجودون</div>
                <div className="rounded-lg bg-amber-50 p-2" data-testid="rakaz-summary-excluded"><div className="text-lg font-bold text-amber-700">{preview.summary.excluded}</div>صفوف مستبعدة</div>
                <div className="rounded-lg bg-slate-100 p-2" data-testid="rakaz-summary-warnings"><div className="text-lg font-bold text-slate-600">{preview.summary.warnings}</div>تحذيرات</div>
              </div>

              {(preview.packages_to_create?.length > 0 || preview.packages_matched?.length > 0) && (
                <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-3" data-testid="rakaz-packages-linking">
                  <div className="mb-2 text-xs font-bold text-teal-700">ربط باقات الاشتراك</div>
                  {preview.packages_matched?.length > 0 && (
                    <div className="mb-2 text-xs text-slate-600">✓ {preview.packages_matched.length} خدمة ستُربط بباقات موجودة: {preview.packages_matched.map((p) => p.name).join('، ')}</div>
                  )}
                  {preview.packages_to_create?.length > 0 && (
                    <div className="text-xs text-slate-600">
                      + ستُنشأ {preview.packages_to_create.length} باقة جديدة في الكتالوج (بلا تكرار):
                      <ul className="mt-1 list-inside list-disc">
                        {preview.packages_to_create.map((pk) => <li key={pk.name} data-testid={`rakaz-package-create-${pk.name}`}>{pk.name} — {pk.price} ر.س ({pk.sample_players_count} لاعب)</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {preview.suggested_new_categories?.length > 0 && (
                <CategoryResolutionSection
                  suggestions={preview.suggested_new_categories}
                  resolutions={rakazState.resolutions}
                  onChange={(name, val) => setRakazState((s) => ({ ...s, resolutions: { ...s.resolutions, [name]: val } }))}
                  categories={categories}
                  testidPrefix="rakaz"
                />
              )}

              <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-100">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr><th className="p-2 text-right">اللاعب</th><th className="p-2 text-right">الفئة/الخدمة</th><th className="p-2 text-right">حالة الاشتراك</th><th className="p-2 text-right">الفرع</th><th className="p-2 text-right">اشتراكات إضافية</th><th className="p-2"></th></tr>
                  </thead>
                  <tbody>
                    {allEntries.map((p) => {
                      const excludedNow = rakazState.excluded.has(p.temp_id);
                      const needsMatch = p.needs_category_match;
                      return (
                        <tr key={p.temp_id} className={`border-t border-slate-100 ${excludedNow ? 'opacity-40' : ''}`} data-testid={`rakaz-preview-row-${p.temp_id}`}>
                          <td className="p-2 font-semibold text-slate-700">{p.name} {p._kind === 'existing' && <span className="font-normal text-blue-500">(موجود مسبقاً)</span>}</td>
                          <td className="p-2 text-slate-600">{p.category_text}{needsMatch && <span className="text-amber-600"> — بحاجة مطابقة</span>}</td>
                          <td className="p-2 text-slate-600">{SUB_STATUS_LABELS[p.subscription_status] || p.subscription_status_raw || '—'}</td>
                          <td className="p-2 text-slate-500">{p.branch_text || '—'}</td>
                          <td className="p-2 text-slate-500">{p.extra_subscriptions_count || 0}</td>
                          <td className="p-2">
                            <button type="button" onClick={() => setRakazState((s) => { const ex = new Set(s.excluded); ex.has(p.temp_id) ? ex.delete(p.temp_id) : ex.add(p.temp_id); return { ...s, excluded: ex }; })} className="text-xs font-semibold text-slate-400 hover:text-red-500" data-testid={`rakaz-toggle-exclude-${p.temp_id}`}>{excludedNow ? 'تراجع' : 'استبعاد'}</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {preview.excluded_rows?.length > 0 && (
                <div className="max-h-28 overflow-y-auto rounded-lg bg-red-50 p-2 text-xs text-red-700" data-testid="rakaz-excluded-rows">
                  {preview.excluded_rows.map((r, i) => <div key={i}>الصف {r.row}: {r.reason}</div>)}
                </div>
              )}
              {preview.warnings?.length > 0 && (
                <div className="max-h-28 overflow-y-auto rounded-lg bg-amber-50 p-2 text-xs text-amber-700" data-testid="rakaz-warnings">
                  {preview.warnings.map((w, i) => <div key={i}>الصف {w.row}: {w.message}</div>)}
                </div>
              )}

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
                <button onClick={() => setRakazState(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600" data-testid="rakaz-cancel-btn">إلغاء</button>
                <PrimaryBtn onClick={confirmRakazImport} disabled={rakazState.busy || allEntries.length === 0} data-testid="rakaz-confirm-btn">{rakazState.busy ? 'جارٍ الاستيراد...' : 'تأكيد الاستيراد'}</PrimaryBtn>
              </div>
            </div>
          );
        })()}
      </Modal>

      <AcademyImportModal
        state={academyState}
        setState={setAcademyState}
        categories={categories}
        onConfirm={confirmAcademyImport}
        onClose={() => setAcademyState(null)}
      />
    </div>
  );
}
